import { useTranslation } from "react-i18next";
import { useState } from "react";
import IconButton from "../../../../components/IconButton/IconButton";
import { Icon } from "../../../../components/Icon/Icon";
import deleteIcon from "../../../../assets/images/bin.svg";
import edit from "../../../../assets/images/pen.svg";
import { Modal } from "../../../../components/Modal/Modal";
import { useGetEmployees } from "../../../../hooks/useGetEmployees";
import { Employee } from "../../../../api/EmployeesAPI";
import EmployeeForm from "../../../../components/EmployeeForm/EmployeeForm";
import PrimaryButton from "../../../../components/PrimaryButton/PrimaryButton";
import { FormType } from "../../../../shared/form.type";
import ConfirmationPopup from "../../../../components/ConfirmationPopup/ConfirmationPopup";
import Card from "../../../../components/Card/Card";
import { PermissionFlags } from "../../../../components/PermissionFlags/PermissionFlags";
import Counter from "../../../../components/Counter/Counter";
import NoDataCTA from "../../../../components/NoDataCTA/NoDataCTA";
import PageWrapper from "../../../PageWrapper/PageWrapper";
import Table, { ColumnDefinition } from "../../../../components/Table/Table";
import Pill from "../../../../components/Pill/Pill";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { PenTool, Trash2, Briefcase, Settings } from "lucide-react";

export type EmployeeColumn = ColumnDefinition<Employee>;

const emptyEmployee: Employee = {
  name: '',
  active: true,
  flags: [],
  note: ''
}

function EmployeesSection() {

  const { t } = useTranslation();

  const [action, setAction] = useState<FormType>('READ');
  const [employee, setEmployee] = useState<Employee | null>();
  const { isLoadingEmployees, employees, addFn, removeFn, updateFn } = useGetEmployees();

  const actionButton = (action: FormType) => {
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.create")!} fn={() => registerAndClose(employee!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.update")!} fn={() => registerAndClose(employee!)} /></span>;
      default:
        return;
    }
  };

  const registerAndClose = async (e: Employee) => {
    if (!!e) {
      if (action === 'CREATE') {
        addFn(e);
      } else {
        updateFn(e);
      }
      setEmployee(null);
    }
  }

  const viewEmployeeDetails = (e: Employee) => {
    setAction('READ');
    setEmployee(e);
  }

  const showNewEmployee = () => {
    setAction('CREATE');
    setEmployee(emptyEmployee);
  }

  const editEmployee = (e: Employee) => {
    setAction('UPDATE');
    setEmployee(e);
  }

  const remove = (e: Employee) => {
    if (!!e) {
      removeFn(e);
      setEmployee(null);
    }
  }

  const getFlagInfo = (flag: string): { icon: React.ReactNode; description: string; color: 'green' | 'red' | 'blue' | 'grey' | 'yellow' | 'orange' } => {
    const flagLower = flag.toLowerCase();
    
    if (flagLower === 'contractsignature') {
      return { icon: <PenTool size={16} />, description: t("admin.employee.flags.contract_signature") || 'Can sign contracts', color: 'blue' };
    }
    
    if (flagLower === 'meetingremoval') {
      return { icon: <Trash2 size={16} />, description: t("admin.employee.flags.meeting_removal") || 'Can remove meetings', color: 'red' };
    }
    
    if (flagLower === 'salesrepresentative') {
      return { icon: <Briefcase size={16} />, description: t("admin.employee.flags.sales_representative") || 'Sales representative', color: 'green' };
    }
    
    return { icon: <Settings size={16} />, description: flag, color: 'grey' };
  }

  const employeeColumns: EmployeeColumn[] = [
    {
      key: 'name',
      title: t("admin.employee.details.name") || "Name",
      render: (employee) => (
        <div className="flex items-center space-x-2">
          <p 
            className="font-medium cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => viewEmployeeDetails(employee)}
          >
            {employee.name || 'Unnamed Employee'}
          </p>
        </div>
      ),
    },
    {
      key: 'active',
      title: t("admin.employee.details.status") || "Status",
      render: (employee) => (
        <div className="w-16 text-center">
          <Pill 
            text={employee.active ? (t("admin.employee.details.status.active") || "Active") : (t("admin.employee.details.status.inactive") || "Inactive")}
            color={employee.active ? 'green' : 'red'}
          />
        </div>
      ),
    },
    {
      key: 'flags',
      title: t("admin.employee.details.flags") || "Flags",
      render: (employee) => (
        <div className="flex flex-wrap gap-1 justify-center">
          {employee.flags && employee.flags.length > 0 ? (
            employee.flags.slice(0, 3).map((flag, index) => {
              const flagInfo = getFlagInfo(flag);
              return (
                <Tooltip key={index} content={flagInfo.description}>
                  <span className="cursor-help text-gray-600 hover:text-gray-800 transition-colors" title={flagInfo.description}>
                    {flagInfo.icon}
                  </span>
                </Tooltip>
              );
            })
          ) : (
            <span className="text-sm text-gray-400">-</span>
          )}
          {employee.flags && employee.flags.length > 3 && (
            <span className="text-xs text-gray-500">+{employee.flags.length - 3}</span>
          )}
        </div>
      ),
    },
    {
      key: 'note',
      title: t("admin.employee.details.note") || "Note",
      render: (employee) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block">
            {employee.note || '-'}
          </span>
        </div>
      ),
    },
  ];

  const renderActions = (employee: Employee, index: number): React.ReactNode => {
    return (
      <div className="flex justify-center space-x-1">
        <PermissionFlags requiredFlags={['update-employees'] || []}>
          <IconButton onClick={() => editEmployee(employee)}>
            <Icon url={edit} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-employees']}>
          <IconButton>
            <ConfirmationPopup onConfirm={() => remove(employee)} confirmText={t("global.confirm") || "Confirm"} title={`${t("global.remove") || "Remove"}: ${employee.name}`}>
              <Icon url={deleteIcon} cssStyles="w-5" />
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </div>
    );
  };

  return (
    <PageWrapper>
      <>
        <PermissionFlags requiredFlags={['see-employees'] || []}>
          <>
            <div className="container flex flex-col md:flex-row my-3 items-stretch space-x-0 md:space-x-5">
              <Card>
                <>
                  <div className="container flex flex-wrap space-x-5 my-3">
                    <PermissionFlags requiredFlags={['create-employees'] || []}>
                      <PrimaryButton text={t("admin.employee.form.new_employee.button")!} fn={showNewEmployee}/>
                    </PermissionFlags>
                  </div>
                </>
              </Card>
              <Counter data={employees.length} label={t("admin.employee.count")}  />
            </div>
            <Card>
              <>
                <div className="container">
                  <Table<Employee>
                    isLoading={isLoadingEmployees}
                    data={employees}
                    columns={employeeColumns}
                    onRowClick={(employee) => {
                      setAction('READ');
                      setEmployee(employee);
                    }}
                    emptyListComponent={
                      <NoDataCTA
                        headline={t("admin.employee.empty.cta.title") || "No Employees Found"}
                        subtext={t("admin.employee.empty.cta.body") || "Create your first employee to get started"}
                        primaryActionText={t("admin.employee.form.new_employee.button") || "New Employee"}
                        onPrimaryAction={showNewEmployee}
                      />
                    }
                    isSelectable={false}
                    selectedItems={[]}
                    compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                    renderActions={renderActions}
                  />
                </div>
              </>
            </Card>
            { employee && 
              <Modal modalTitle={employee.name || "admin.employee.form.new_employee"} canClose onClose={() => setEmployee(null)}>
                <EmployeeForm employee={employee} onEmployeeForm={setEmployee} actionButton={actionButton(action)} isDisabled={action === 'READ'}/>
              </Modal>
            }
          </>
        </PermissionFlags>
      </>
    </PageWrapper>
  );
}

export default EmployeesSection;
